import type { BusRoute, Direction, UserProfile } from "@bus/shared";
import { Bell, ChevronRight, Heart, LogOut, Map, Search, Settings2, UserRound, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

interface RouteDrawerProps {
  open: boolean;
  routes: BusRoute[];
  activeRouteId: string;
  profile: UserProfile | null | undefined;
  onClose: () => void;
  onSelectRoute: (routeId: string, direction: Direction) => void;
  onToggleFavorite: (routeId: string) => void;
  onOpenAccount: () => void;
  onOpenTracking: () => void;
  onOpenSettings: () => void;
  onLogout: () => Promise<void>;
}

export function RouteDrawer({ open, routes, activeRouteId, profile, onClose, onSelectRoute, onToggleFavorite, onOpenAccount, onOpenTracking, onOpenSettings, onLogout }: RouteDrawerProps) {
  const [query, setQuery] = useState("");
  const closeRef = useRef<HTMLButtonElement>(null);
  const favorites = new Set(profile?.preferences.favoriteRouteIds ?? []);
  const filtered = useMemo(() => routes.filter((route) => `${route.name}${route.departure}${route.destination}`.toLowerCase().includes(query.trim().toLowerCase())).sort((a, b) => Number(favorites.has(b.id)) - Number(favorites.has(a.id))), [routes, query, profile?.preferences.favoriteRouteIds]);
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const listener = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    addEventListener("keydown", listener); return () => removeEventListener("keydown", listener);
  }, [open, onClose]);
  if (!open) return null;
  return <div className="drawer-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <aside className="route-drawer" role="dialog" aria-modal="true" aria-labelledby="drawer-title">
      <header><div><small>BUS NOW</small><h2 id="drawer-title">路線與帳號</h2></div><button ref={closeRef} type="button" onClick={onClose} aria-label="關閉選單"><X size={21} /></button></header>
      <label className="drawer-search"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜尋公車路線" aria-label="搜尋公車路線" /></label>
      <nav className="drawer-routes" aria-label="公車路線">
        <span className="drawer-section-label"><Map size={14} />{favorites.size ? "常用與全部路線" : "全部路線"}</span>
        {filtered.map((route) => <div className={`drawer-route${route.id === activeRouteId ? " is-active" : ""}`} key={route.id}>
          <button type="button" onClick={() => { onSelectRoute(route.id, profile?.preferences.defaultDirection ?? 0); onClose(); }}><i style={{ background: route.color }} /><span><strong>{route.name}</strong><small>{route.departure} → {route.destination}</small></span><ChevronRight size={17} /></button>
          <button type="button" className={favorites.has(route.id) ? "is-favorite" : ""} onClick={() => onToggleFavorite(route.id)} aria-label={favorites.has(route.id) ? `取消常用 ${route.name}` : `加入常用 ${route.name}`}><Heart size={17} fill={favorites.has(route.id) ? "currentColor" : "none"} /></button>
        </div>)}
      </nav>
      <div className="drawer-account">
        <button type="button" className="account-card" onClick={() => { onOpenAccount(); onClose(); }}><span>{profile?.email?.[0]?.toUpperCase() ?? <UserRound size={18} />}</span><div><strong>{profile?.displayName || "個人化準備中"}</strong><small>{profile?.email || "Google 登入將先於預覽站啟用"}</small></div><ChevronRight size={17} /></button>
        <button type="button" onClick={() => { onOpenTracking(); onClose(); }}><Bell size={17} />通勤提醒</button>
        <button type="button" onClick={() => { onOpenSettings(); onClose(); }}><Settings2 size={17} />住家與工作站</button>
        {profile ? <button type="button" onClick={() => { onClose(); void onLogout(); }}><LogOut size={17} />登出 Google 帳號</button> : null}
      </div>
    </aside>
  </div>;
}
